import React, { FC, useEffect, useMemo, useState } from 'react';
import {
	BadgeStudioBarCode,
	BadgeStudioImage,
	BadgeStudioMagstripe,
	E_ObjectTypes,
	GenericGraphicObject,
	GraphicCommandSet,
	GraphicLink,
	GraphicText,
	PointMonitoringZone,
	VelocityObject,
} from '../../../model/GraphicModel';

import type { TabsProps } from 'antd';
import { Empty, Form, Spin, Tabs } from 'antd';
import { WithCollapsible } from '..';
import { handleResponse } from '../../../Helper';
import { enrollmentApi, graphicApi } from '../../../api';
import { Logger } from '../../../model/LoggingModel';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setFontsAction, setUDFsAction, setUDFs_256_Action } from '../../../store/badge/actions';
import { fontFamiliesSelector, udfs256Selector, udfsSelector } from '../../../store/badge/selectors';
import { buildMDObjectInputs, buildNameRow, isFontAvailable } from './helper';
import styles from './propertiesWindow.module.scss';

type Props = {
	currentObject:
		| GenericGraphicObject
		| GraphicText
		| VelocityObject
		| PointMonitoringZone
		| GraphicCommandSet
		| GraphicLink
		| BadgeStudioImage
		| BadgeStudioBarCode
		| BadgeStudioMagstripe;
	currentObjectType: E_ObjectTypes;
	isBadge?: boolean;
	updateItem: (obj: GenericGraphicObject) => void;
};

const PropertiesWindowComponent: FC<Props> = ({ currentObject, currentObjectType, isBadge, updateItem }) => {
	const dispatch = useStoreDispatch();
	const [tabs, setTabs] = useState<TabsProps['items']>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	// const [isMounted, setIsMounted] = useState<boolean>(false);
	const [form] = Form.useForm();
	const tempGenericObject = useMemo(() => currentObject as GenericGraphicObject, [currentObject]);
	const fontFamilies = useStoreSelector(fontFamiliesSelector);
	const udfs = useStoreSelector(udfsSelector);
	const udfs256 = useStoreSelector(udfs256Selector);

	useEffect(() => {
		if (currentObjectType !== E_ObjectTypes.OTYP_NONE) {
			if (fontFamilies === null) {
				graphicApi.getFontList().then(response => {
					if (!handleResponse(response)) {
						const newFontFamilies = response.Entity.filter(ff => isFontAvailable(ff));
						dispatch(setFontsAction(newFontFamilies));
					}
				});
			}

			if (udfs === null) {
				enrollmentApi.getUserDefinedFields().then(response => {
					if (!handleResponse(response)) {
						dispatch(setUDFsAction(response.Entity));
					}
				});
			}

			if (udfs256 === null) {
				graphicApi.getUdfFields().then(response => {
					if (!handleResponse(response)) {
						dispatch(setUDFs_256_Action(response.Entity));
					}
				});
			}
		} else {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (fontFamilies && udfs && udfs256) {
			buildTabs();
		}
	}, [fontFamilies, tempGenericObject?.['NarrowBarWidth'], udfs, udfs256]);

	const buildTabs = async () => {
		try {
			const sections: TabsProps['items'] = [];

			if (currentObjectType !== E_ObjectTypes.OTYP_NONE) {
				const sectionsMap: Map<string, JSX.Element[]> = new Map<string, JSX.Element[]>();
				switch (currentObjectType) {
					case E_ObjectTypes.OTYP_MDOBJECT: {
						await buildMDObjectInputs({
							currentObject: tempGenericObject,
							fontFamilies,
							isBadge,
							sectionsMap,
							tempGenericObject,
							onChangeColorPicker,
							udfs256,
							udfs,
						});

						break;
					}
					case E_ObjectTypes.OTYP_CANVAS: {
						// TODO: If Len(tvDirectory.SelectedItem.Key) > 3 And Left(tvDirectory.SelectedItem.Key, 3) = "VBD" Then
						if (true) {
							// TODO: Retrive name of canvas/map, is this needed?
							sectionsMap.set(_('General'), [buildNameRow(tempGenericObject?.Name, true)]);
						}
						break;
					}
				}

				sectionsMap.forEach((v, k) => {
					sections.push({
						forceRender: true,
						key: k,
						label: k,
						children: <div className={styles.tabContent}>{v}</div>,
					});
				});
			}

			setTabs(sections);
		} catch (error) {
			Logger.writeErrorLog(error);
		} finally {
			setIsLoading(false);
		}
	};

	const onChangeColorPicker = (inputName: string[], value: string) => {
		let newObj = form.getFieldsValue();
		if (inputName.length == 2) {
			form.setFieldsValue({
				[inputName[0]]: {
					[inputName[1]]: value,
				},
			});
			newObj[inputName[0]][inputName[1]] = value;
		} else {
			form.setFieldValue(inputName[0], value);
			newObj[inputName[0]] = value;
		}
		updateItem(newObj);
	};

	const handleOnFormChange = (_, values) => {
		const obj = {
			...values,
		};
		updateItem(obj);
	};

	return (
		<div id='propertiesWindow' className={styles.propertiesWindow} style={{ justifyContent: tabs?.length > 0 ? 'unset' : 'center' }}>
			<Spin spinning={isLoading} tip={_('Loading')}>
				<Form autoComplete='off' colon={false} form={form} labelWrap onValuesChange={handleOnFormChange} className={styles.form}>
					{tabs?.length > 0 ? (
						<Tabs defaultActiveKey='1' items={tabs} />
					) : (
						<div className={styles.noDataContainer}>
							<Empty description={<div className={styles.noData}>{_('PropsWindowNoData')}</div>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
						</div>
					)}
				</Form>
			</Spin>
		</div>
	);
};

const PropertiesWindow = WithCollapsible(PropertiesWindowComponent, _('Properties'));

export { PropertiesWindow };
